<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useDebounceFn } from '@vueuse/core';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);
const suspendConfirmDialogRef = ref(null);
const pendingSuspendConversationId = ref(null);
const pendingBulkSuspend = ref(null);

provide('contextMenuElementTarget', virtualListRef);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.PRIORITY_DESC);
const searchQuery = ref('');
const searchResultChats = ref([]);
const searchLoading = ref(false);
const savedAssigneeTabBeforeSearch = ref(null);
const isSearchMode = computed(() => searchQuery.value.trim().length > 0);
const isSearchReady = computed(() => searchQuery.value.trim().length >= 3);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const favoriteChatsList = useMapGetter('getFavoriteChats');
const groupChatsList = useMapGetter('getGroupChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const myTeamsList = useMapGetter('teams/getMyTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations: performBulkUpdateConversations,
} = useBulkActions();

const onUpdateConversations = (status, snoozedUntil) => {
  if (status === wootConstants.STATUS_TYPE.SUSPENDED) {
    pendingSuspendConversationId.value = null;
    pendingBulkSuspend.value = { status, snoozedUntil };
    suspendConfirmDialogRef.value?.open();
    return;
  }
  performBulkUpdateConversations(status, snoozedUntil);
};

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const resolveDefaultAssigneeTab = () => {
  const allowedTabs = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  );

  if (allowedTabs.some(item => item.key === wootConstants.ASSIGNEE_TYPE.ALL)) {
    return wootConstants.ASSIGNEE_TYPE.ALL;
  }

  return allowedTabs[0]?.key || wootConstants.ASSIGNEE_TYPE.ME;
};


const assigneeTabIcon = key => {
  const icons = {
    [wootConstants.ASSIGNEE_TYPE.FAVORITES]: 'i-ri-star-fill',
    [wootConstants.ASSIGNEE_TYPE.ME]: 'i-lucide-user',
    [wootConstants.ASSIGNEE_TYPE.UNASSIGNED]: 'i-lucide-users-round',
    [wootConstants.ASSIGNEE_TYPE.ALL]: 'i-lucide-list',
    [wootConstants.ASSIGNEE_TYPE.GROUPS]: 'i-lucide-users',
    search: 'i-lucide-search',
    conversations: 'i-lucide-bot',
  };
  return icons[key] || '';
};

const assigneeTabItems = computed(() => {
  if (isSearchMode.value) {
    return [
      {
        key: 'search',
        name: t('CHAT_LIST.SEARCH.TAB'),
        icon: assigneeTabIcon('search'),
        count: isSearchReady.value ? searchResultChats.value.length : 0,
        unreadCount: 0,
      },
    ];
  }

  // @Menções: only "Todas"
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return [
      {
        key: wootConstants.ASSIGNEE_TYPE.ALL,
        name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
        icon: assigneeTabIcon(wootConstants.ASSIGNEE_TYPE.ALL),
        count: conversationStats.value.allCount || 0,
        unreadCount: conversationStats.value.allUnreadCount || 0,
      },
    ];
  }

  // Chatbot: "Conversas" (bot ativo, sem grupos) + "Grupos"
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.CHATBOT) {
    return [
      {
        key: wootConstants.ASSIGNEE_TYPE.ALL,
        name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.conversations'),
        icon: assigneeTabIcon('conversations'),
        count: conversationStats.value.allCount || 0,
        unreadCount: conversationStats.value.allUnreadCount || 0,
      },
      {
        key: wootConstants.ASSIGNEE_TYPE.GROUPS,
        name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.groups'),
        icon: assigneeTabIcon(wootConstants.ASSIGNEE_TYPE.GROUPS),
        count: conversationStats.value.groupsCount || 0,
        unreadCount: conversationStats.value.groupsUnreadCount || 0,
      },
    ];
  }

  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey, unreadCount: unreadCountKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    icon: assigneeTabIcon(key),
    count: conversationStats.value[countKey] || 0,
    unreadCount: conversationStats.value[unreadCountKey] || 0,
  }));
});

const showConversationSearch = computed(() => {
  return (
    Boolean(props.conversationInbox) &&
    !hasAppliedFiltersOrActiveFolders.value &&
    props.conversationType !== wootConstants.CONVERSATION_TYPE.MENTION
  );
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    [
      wootConstants.ASSIGNEE_TYPE.ALL,
      wootConstants.ASSIGNEE_TYPE.ME,
      wootConstants.ASSIGNEE_TYPE.FAVORITES,
    ].includes(activeAssigneeTab.value)
  );
});

const conversationListFilterKey = computed(() => {
  if (hasAppliedFiltersOrActiveFolders.value) {
    return 'appliedFilters';
  }

  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return wootConstants.ASSIGNEE_TYPE.ALL;
  }

  return activeAssigneeTab.value;
});

const currentPageFilterKey = conversationListFilterKey;

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  conversationListFilterKey
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return conversationStats.value.allCount;
  }

  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const listIsEmpty =
    Array.isArray(chatsOnView.value) && !chatsOnView.value.length;
  // Tab counts are open-only; with status=all the real list is much larger.
  // Only force page 1 on a fresh reset (currentPage === 0). Re-forcing page 1
  // after a full API page was filtered client-side causes an infinite reload loop.
  const isUnderPerPage =
    listIsEmpty &&
    currentPage.value === 0 &&
    !hasAppliedFiltersOrActiveFolders.value &&
    activeAssigneeTabCount.value > 0 &&
    activeAssigneeTabCount.value < conversationsPerPage;

  if (isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const isMyTeamsScope = computed(
  () => String(props.teamId) === wootConstants.MY_TEAMS_SCOPE
);

const conversationFilters = computed(() => {
  const isMentionView =
    props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION;
  const isChatbotView =
    props.conversationType === wootConstants.CONVERSATION_TYPE.CHATBOT;
  const isUnassignedTab =
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED;

  let assigneeType = activeAssigneeTab.value;
  if (isSearchReady.value || isMentionView) {
    assigneeType = wootConstants.ASSIGNEE_TYPE.ALL;
  } else if (isChatbotView) {
    assigneeType =
      activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.GROUPS
        ? wootConstants.ASSIGNEE_TYPE.GROUPS
        : wootConstants.ASSIGNEE_TYPE.ALL;
  }

  // Fila always shows open+unassigned (waiting). Other tabs use status filter.
  let status = activeStatus.value;
  if (isSearchReady.value) {
    status = wootConstants.STATUS_TYPE.ALL;
  } else if (isUnassignedTab) {
    status = wootConstants.STATUS_TYPE.OPEN;
  }

  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType,
    status,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    myTeamIds: isMyTeamsScope.value
      ? myTeamsList.value.map(team => team.id)
      : undefined,
    currentUserId: isMyTeamsScope.value ? currentUser.value?.id : undefined,
    conversationType: props.conversationType || undefined,
    q: isSearchReady.value ? searchQuery.value.trim() : undefined,
  };
});

const activeTeam = computed(() => {
  if (isMyTeamsScope.value) {
    return { name: t('SIDEBAR.ALL_TEAMS_CONVERSATIONS') };
  }
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const channelDisplayName = name => (name || '').toLocaleUpperCase('pt-BR');

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name && props.teamId && activeTeam.value.name) {
    return `${channelDisplayName(inbox.value.name)} - ${activeTeam.value.name}`;
  }
  if (
    inbox.value.name &&
    !props.teamId &&
    props.conversationType === wootConstants.CONVERSATION_TYPE.CHATBOT
  ) {
    return `${channelDisplayName(inbox.value.name)} - ${t('SIDEBAR.CHATBOT_CONVERSATIONS')}`;
  }
  if (
    inbox.value.name &&
    !props.teamId &&
    props.conversationType !== wootConstants.CONVERSATION_TYPE.MENTION &&
    props.conversationType !== wootConstants.CONVERSATION_TYPE.CHATBOT
  ) {
    return `${channelDisplayName(inbox.value.name)} - ${t('SIDEBAR.ALL_CONVERSATIONS')}`;
  }
  if (inbox.value.name) {
    return channelDisplayName(inbox.value.name);
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

function isWhatsAppGroupConversation(conversation) {
  return String(conversation.meta?.sender?.identifier || '').includes('@g.us');
}

function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(
      c => !c.meta?.assignee && c.status === wootConstants.STATUS_TYPE.OPEN
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.FAVORITES) {
    return conversations.filter(c => c.favorited);
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.GROUPS) {
    return conversations.filter(isWhatsAppGroupConversation);
  }
  return [...conversations];
}

const PRIORITY_SORT_RANK = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};

function isClosedConversation(conversation) {
  return (
    conversation.status === wootConstants.STATUS_TYPE.RESOLVED ||
    conversation.service_suspended === true
  );
}

function sortConversationsByStatus(conversations) {
  const isPriorityDesc =
    activeSortBy.value === wootConstants.SORT_BY_TYPE.PRIORITY_DESC;
  const isPriorityAsc =
    activeSortBy.value === wootConstants.SORT_BY_TYPE.PRIORITY_ASC;
  const isPrioritySort = isPriorityDesc || isPriorityAsc;

  return [...conversations].sort((a, b) => {
    // Open / pending / snoozed first; resolved / suspended last
    const aClosed = isClosedConversation(a) ? 1 : 0;
    const bClosed = isClosedConversation(b) ? 1 : 0;
    if (aClosed !== bClosed) return aClosed - bClosed;

    if (isPrioritySort) {
      const aRank = PRIORITY_SORT_RANK[a.priority] || 0;
      const bRank = PRIORITY_SORT_RANK[b.priority] || 0;
      if (aRank !== bRank) {
        return isPriorityAsc ? aRank - bRank : bRank - aRank;
      }
      return (b.timestamp || 0) - (a.timestamp || 0);
    }

    return 0;
  });
}

const conversationList = computed(() => {
  if (isSearchReady.value) {
    // Search results live outside the filtered getters. After favoriting,
    // Vuex is updated but searchResultChats keeps the previous snapshot —
    // re-read favorited from the store so the star stays in sync.
    return searchResultChats.value.map(chat => {
      if (chat?.contact_only || !chat?.id) return chat;
      const storeChat = getConversationById.value(chat.id);
      if (!storeChat || storeChat.favorited === chat.favorited) return chat;
      return { ...chat, favorited: storeChat.favorited };
    });
  }

  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION
    ) {
      localConversationList = [...allChatList.value(filters)];
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (
      activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.FAVORITES
    ) {
      localConversationList = [...favoriteChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.GROUPS) {
      localConversationList = [...groupChatsList.value(filters)];
    } else if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.CHATBOT
    ) {
      // Conversas: bot ativo e não-grupo (API já filtra; reforço no client)
      localConversationList = allChatList
        .value(filters)
        .filter(c => !isWhatsAppGroupConversation(c));
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return sortConversationsByStatus(localConversationList);
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function resolveDefaultStatus(savedStatus) {
  // Default is always "all statuses". Legacy UI setting "open" is treated
  // as unset so everyone (admin + agent) sees open + pending + resolved.
  if (!savedStatus || savedStatus === wootConstants.STATUS_TYPE.OPEN) {
    return wootConstants.STATUS_TYPE.ALL;
  }
  return savedStatus;
}

function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = resolveDefaultStatus(status);
  // Default: highest priority first (same option as in the sort select).
  const validSortOptions = Object.values(wootConstants.SORT_BY_TYPE);
  activeSortBy.value = validSortOptions.includes(orderBy)
    ? orderBy
    : wootConstants.SORT_BY_TYPE.PRIORITY_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function mapContactToSearchCard(contact) {
  const score = Number(contact.search_score || 0);
  return {
    id: `contact-${contact.id}`,
    contact_only: true,
    contact_id: contact.id,
    inbox_id: Number(props.conversationInbox),
    meta: { sender: contact },
    messages: [],
    unread_count: 0,
    status: 'open',
    timestamp: contact.last_activity_at || 0,
    team_id: null,
    can_reply: false,
    search_score: score,
    // Name matches share bucket 2 with closed name-matched conversations.
    // Phone/email-only contacts stay after every conversation match.
    search_bucket: score > 100 ? 2 : 0,
  };
}

async function fetchInboxSearchResults() {
  if (!isSearchReady.value || !props.conversationInbox) {
    searchResultChats.value = [];
    return;
  }

  searchLoading.value = true;
  try {
    const { data } = await ConversationApi.inboxSearch({
      q: searchQuery.value.trim(),
      inboxId: props.conversationInbox,
      teamId: props.teamId || undefined,
      page: 1,
    });
    const payload = data?.data?.payload || [];
    const contacts = data?.data?.contacts || [];
    // Keep conversations in the vuex store so opening a chat works normally
    payload.forEach(conversation => {
      if (conversation?.meta?.sender) {
        store.dispatch('updateConversation', conversation);
      }
    });

    const conversationContactIds = new Set(
      payload.map(item => item.meta?.sender?.id).filter(Boolean)
    );
    const contactCards = contacts
      .filter(contact => !conversationContactIds.has(contact.id))
      .map(mapContactToSearchCard);

    const scoreOf = chat => Number(chat?.search_score || 0);
    const activityOf = chat => Number(chat?.timestamp || 0);
    const byScoreThenActivity = (a, b) =>
      scoreOf(b) - scoreOf(a) || activityOf(b) - activityOf(a);
    const byActivity = (a, b) => activityOf(b) - activityOf(a);
    const inBucket = bucket =>
      payload.filter(chat => Number(chat?.search_bucket) === bucket);
    const nameContactCards = contactCards.filter(card => card.search_bucket === 2);
    const otherContactCards = contactCards.filter(card => card.search_bucket !== 2);
    const group2 = [...inBucket(2), ...nameContactCards].sort(byScoreThenActivity);

    // 1 name + active conversation, 2 name without an active conversation,
    // 3 message matches on open chats, 4 message matches on resolved/suspended.
    searchResultChats.value = [
      ...inBucket(1).sort(byScoreThenActivity),
      ...group2,
      ...inBucket(3).sort(byActivity),
      ...inBucket(4).sort(byActivity),
      ...payload.filter(chat => ![1, 2, 3, 4].includes(Number(chat?.search_bucket))),
      ...otherContactCards,
    ];
    emitConversationLoaded();
  } catch (_error) {
    searchResultChats.value = [];
  } finally {
    searchLoading.value = false;
  }
}

async function fetchConversations() {
  if (isSearchReady.value) {
    await fetchInboxSearchResults();
    return;
  }
  // Await so fetchAllConversations reads the status/page just selected
  // (e.g. status=all), not a stale conversationFilters snapshot.
  await store.dispatch('updateChatListFilters', conversationFilters.value);
  await store.dispatch('fetchAllConversations');
  emitConversationLoaded();
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

const runDebouncedSearch = useDebounceFn(() => {
  fetchInboxSearchResults();
}, 300);

function onSearchQueryUpdate(value) {
  const next = value || '';
  const wasSearch = searchQuery.value.trim().length > 0;
  searchQuery.value = next;
  const isSearch = next.trim().length > 0;

  if (isSearch && !wasSearch) {
    savedAssigneeTabBeforeSearch.value = activeAssigneeTab.value;
    activeAssigneeTab.value = 'search';
  } else if (!isSearch && wasSearch) {
    activeAssigneeTab.value =
      savedAssigneeTabBeforeSearch.value || resolveDefaultAssigneeTab();
    savedAssigneeTabBeforeSearch.value = null;
    searchResultChats.value = [];
    resetAndFetchData();
    return;
  }

  if (next.trim().length >= 3) {
    runDebouncedSearch();
  } else {
    searchResultChats.value = [];
  }
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    store.dispatch('updateChatListFilters', conversationFilters.value);
    store.dispatch('conversationPage/reset');
    store.dispatch('emptyAllConversations');
    fetchConversations();
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  } else if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.CHATBOT
  ) {
    conversationType = wootConstants.CONVERSATION_TYPE.CHATBOT;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function unsuspendConversation(conversationId) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      serviceSuspended: false,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status === wootConstants.STATUS_TYPE.SUSPENDED) {
    pendingSuspendConversationId.value = conversationId;
    suspendConfirmDialogRef.value?.open();
    return;
  }

  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch(
    'conversationStats/fetchScopedMeta',
    conversationFilters.value
  );
});

onMounted(() => {
  activeAssigneeTab.value = resolveDefaultAssigneeTab();
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  store.dispatch('setChatListFilters', conversationFilters.value);
  // Persist default "all" so legacy "open" preference does not come back
  const { conversations_filter_by: filterBy = {} } = uiSettings.value || {};
  if (
    filterBy.status !== activeStatus.value ||
    filterBy.order_by !== activeSortBy.value
  ) {
    updateUISettings({
      conversations_filter_by: {
        status: activeStatus.value,
        order_by: activeSortBy.value,
      },
    });
  }
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

const confirmSuspendConversation = () => {
  const conversationId = pendingSuspendConversationId.value;
  const bulk = pendingBulkSuspend.value;
  suspendConfirmDialogRef.value?.close();
  pendingSuspendConversationId.value = null;
  pendingBulkSuspend.value = null;
  if (bulk) {
    performBulkUpdateConversations(bulk.status, bulk.snoozedUntil);
    return;
  }
  if (!conversationId) return;
  toggleConversationStatus(
    conversationId,
    wootConstants.STATUS_TYPE.SUSPENDED,
    null
  );
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('unsuspendConversation', unsuspendConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

function clearSearchAndReload() {
  searchQuery.value = '';
  searchResultChats.value = [];
  if (activeAssigneeTab.value === 'search') {
    activeAssigneeTab.value = resolveDefaultAssigneeTab();
  }
  resetAndFetchData();
}

watch(activeTeam, () => clearSearchAndReload());

watch(
  computed(() => props.conversationInbox),
  () => clearSearchAndReload()
);
watch(
  computed(() => props.label),
  () => clearSearchAndReload()
);
watch(
  computed(() => props.teamId),
  () => clearSearchAndReload()
);
watch(
  computed(() => props.conversationType),
  () => {
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION ||
      props.conversationType === wootConstants.CONVERSATION_TYPE.CHATBOT
    ) {
      activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
    }
    clearSearchAndReload();
  }
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(
  conversationList,
  list => {
    chatsOnView.value = list;
  },
  { flush: 'post' }
);

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="
        (chatListLoading || searchLoading) && !conversationList.length
      "
      :search-query="searchQuery"
      :show-search="showConversationSearch"
      :inbox-id="conversationInbox"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @update:search-query="onSearchQueryUpdate"
    >
      <template
        v-if="!hasAppliedFiltersOrActiveFolders && !isSearchMode"
        #tabs="{ onChannelSurface, accentColor }"
      >
        <ChatTypeTabs
          :items="assigneeTabItems"
          :active-tab="activeAssigneeTab"
          :on-channel-surface="onChannelSurface"
          :accent-color="accentColor"
          is-compact
          @chat-tab-change="updateAssigneeTab"
        />
      </template>
    </ChatListHeader>

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <p
      v-if="isSearchMode && !isSearchReady"
      class="flex overflow-auto justify-center items-center p-4 text-n-slate-11"
    >
      {{ $t('CHAT_LIST.SEARCH.MIN_CHARS') }}
    </p>
    <p
      v-else-if="!chatListLoading && !searchLoading && !conversationList.length"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="flex-1 min-h-0 overflow-y-auto conversations-list no-scrollbar"
      :class="{ '!overflow-hidden': isContextMenuOpen }"
    >
      <Virtualizer
        ref="virtualListRef"
        v-slot="{ item, index }"
        :data="conversationList"
      >
        <ConversationItem
          :source="item"
          :label="label"
          :team-id="teamId"
          :folders-id="foldersId"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :data-index="index"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </Virtualizer>
      <div v-if="chatListLoading" class="flex justify-center my-4">
        <Spinner class="text-n-brand" />
      </div>
      <p
        v-else-if="showEndOfListMessage"
        class="p-4 text-center text-n-slate-11"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
      <IntersectionObserver
        v-else
        :options="intersectionObserverOptions"
        @observed="loadMoreConversations"
      />
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <Dialog
      ref="suspendConfirmDialogRef"
      type="alert"
      :title="$t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND')"
      :description="$t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND_CONFIRM')"
      :confirm-button-label="
        $t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND_CONFIRM_OK')
      "
      :cancel-button-label="
        $t('CONVERSATION.RESOLVE_DROPDOWN.SUSPEND_CONFIRM_CANCEL')
      "
      @confirm="confirmSuspendConversation"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
